import React from 'react';
import { Check, Sparkles, Zap, Crown } from 'lucide-react';
import { Card } from './ui/Card';
import { Button } from './ui/Button';
import { useTranslations } from '../hooks/useTranslations';
interface PricingPlan {
  name: string;
  price: string;
  period?: string;
  initialFee?: string;
  subtitle?: string;
  features: string[];
  highlighted?: boolean;
  icon: React.ReactNode;
}
export function PricingSection() {
  const {
    t
  } = useTranslations();
  const scrollToContact = () => {
    const contactSection = document.getElementById('contact');
    contactSection?.scrollIntoView({
      behavior: 'smooth',
      block: 'start'
    });
  };
  const plans: PricingPlan[] = [{
    name: 'Plan Gold',
    price: '$119',
    period: t('pricing.perMonth'),
    initialFee: t('pricing.goldFee'),
    features: [t('pricing.gold.feature1'), t('pricing.gold.feature2'), t('pricing.gold.feature3'), t('pricing.gold.feature4'), t('pricing.gold.feature5')],
    icon: <Sparkles className="w-6 h-6" data-id="element-352" />
  }, {
    name: 'Plan Platinum',
    price: '$149',
    period: t('pricing.perMonth'),
    initialFee: t('pricing.platinumFee'),
    features: [t('pricing.platinum.feature1'), t('pricing.platinum.feature2'), t('pricing.platinum.feature3'), t('pricing.platinum.feature4'), t('pricing.platinum.feature5'), t('pricing.platinum.feature6')],
    highlighted: true,
    icon: <Crown className="w-6 h-6" data-id="element-353" />
  }, {
    name: 'Plan Express VIP',
    price: '$1,499',
    subtitle: t('pricing.oneTime'),
    features: [t('pricing.vip.feature1'), t('pricing.vip.feature2'), t('pricing.vip.feature3'), t('pricing.vip.feature4'), t('pricing.vip.feature5')],
    highlighted: true,
    icon: <Zap className="w-6 h-6" data-id="element-354" />
  }, {
    name: 'Plan Express VIP+',
    price: '$1,999',
    subtitle: t('pricing.oneTime'),
    features: [t('pricing.vipPlus.feature1'), t('pricing.vipPlus.feature2'), t('pricing.vipPlus.feature3'), t('pricing.vipPlus.feature4'), t('pricing.vipPlus.feature5'), t('pricing.vipPlus.feature6')],
    icon: <Crown className="w-6 h-6" data-id="element-355" />
  }];
  return <section id="pricing" className="py-20 bg-white" data-id="element-356">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-357">
        <div className="text-center mb-16" data-id="element-358">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4" data-id="element-359">
            {t('pricing.title')}
          </h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto" data-id="element-360">
            {t('pricing.subtitle')}
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6" data-id="element-361">
          {plans.map((plan, index) => <Card key={plan.name} highlighted={plan.highlighted} className={`relative flex flex-col p-6 animate-fade-in-up ${plan.highlighted ? 'lg:-mt-4 lg:mb-4' : ''}`} style={{
          animationDelay: `${index * 0.1}s`
        }} data-id="element-362">
              {plan.highlighted && index === 1 && <div className="absolute -top-4 left-1/2 -translate-x-1/2" data-id="element-363">
                  <span className="inline-flex items-center px-4 py-1 rounded-full bg-gradient-to-r from-purple-600 to-fuchsia-500 text-white text-sm font-semibold shadow-lg" data-id="element-364">
                    {t('pricing.mostPopular')}
                  </span>
                </div>}

              <div className={`w-12 h-12 rounded-xl flex items-center justify-center mb-4 ${plan.highlighted ? 'bg-gradient-to-br from-purple-600 to-fuchsia-500 text-white' : 'bg-purple-100 text-purple-600'}`} data-id="element-365">
                {plan.icon}
              </div>

              <h3 className="text-xl font-bold text-gray-900 mb-2" data-id="element-366">
                {plan.name}
              </h3>

              <div className="mb-4" data-id="element-367">
                <span className="text-4xl font-bold text-gray-900" data-id="element-368">
                  {plan.price}
                </span>
                {plan.period && <span className="text-gray-500" data-id="element-369">{plan.period}</span>}
                {plan.subtitle && <p className="text-sm text-gray-500 mt-1" data-id="element-370">{plan.subtitle}</p>}
                {plan.initialFee && <p className="text-sm text-purple-600 font-medium mt-1" data-id="element-371">
                    {plan.initialFee}
                  </p>}
              </div>

              <ul className="space-y-3 mb-6 flex-grow" data-id="element-372">
                {plan.features.map((feature, i) => <li key={i} className="flex items-start" data-id="element-373">
                    <Check className={`w-5 h-5 mr-2 flex-shrink-0 ${plan.highlighted ? 'text-purple-500' : 'text-purple-400'}`} data-id="element-374" />
                    <span className="text-gray-600 text-sm" data-id="element-375">{feature}</span>
                  </li>)}
              </ul>

              <Button variant={plan.highlighted ? 'primary' : 'outline'} fullWidth onClick={scrollToContact} data-id="element-376">
                {t('pricing.cta')}
              </Button>
            </Card>)}
        </div>

        <div className="mt-12 text-center" data-id="element-377">
          <p className="text-gray-500 text-sm" data-id="element-378">{t('pricing.note')}</p>
        </div>
      </div>
    </section>;
}